import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid2';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Snackbar from '@mui/material/Snackbar';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoIcon from '@mui/icons-material/Info';
import BlockIcon from '@mui/icons-material/Block';
import BadgeIcon from '@mui/icons-material/Badge';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import EmergencyIcon from '@mui/icons-material/ContactEmergency';
import Chip from '@mui/material/Chip';
import { adminApi, adminKeys } from '@/features/admin/api/admin.api';
import { AdminPageHeader } from '@/features/admin/components/AdminPageHeader';
import { GuideStatusChip } from '@/features/admin/components/GuideStatusChip';
import { IdentityCardStateChip } from '@/features/admin/components/IdentityCardStateChip';
import { ADMIN_ROUTES } from '@/features/admin/constants/routes';
import {
  VerificationActionDialog,
  type VerificationAction,
} from '@/features/admin/components/VerificationActionDialog';
import { StatusBadge } from '@/shared/components/ui/StatusBadge';
import {
  formatStatusLabel,
  verificationStatusToBadge,
} from '@/shared/utils/statusBadge';
import {
  DocumentType,
  GuideIdentityStatus,
  GuideStatus,
  MarketplaceStatus,
  ProfileStatus,
  VerificationStatus,
} from '@/types';
import type { GuideDocument } from '@/types';
import { copyTextToClipboard } from '@/features/guide-identity/utils/identityUrl';
import { GUIDE_EXPERIENCE_LABEL } from '@/features/guide-profile/constants';

type ActionType =
  | 'approve'
  | 'reject'
  | 'request-info'
  | 'suspend'
  | 'identity-activate'
  | 'identity-suspend'
  | 'identity-revoke';

const ACTION_CONFIG: Record<
  ActionType,
  {
    label: string;
    color: 'success' | 'error' | 'warning' | 'inherit';
    icon: React.ReactNode;
    title: string;
    successMessage: string;
  }
> = {
  approve: {
    label: 'Approve',
    color: 'success',
    icon: <CheckCircleIcon />,
    title: 'Approve Guide',
    successMessage: 'Guide approved successfully. Profile is now live.',
  },
  reject: {
    label: 'Reject',
    color: 'error',
    icon: <CancelIcon />,
    title: 'Reject Guide',
    successMessage: 'Guide application rejected. The guide has been notified.',
  },
  'request-info': {
    label: 'Request Info',
    color: 'warning',
    icon: <InfoIcon />,
    title: 'Request More Information',
    successMessage: 'Information request sent to the guide.',
  },
  suspend: {
    label: 'Suspend',
    color: 'inherit',
    icon: <BlockIcon />,
    title: 'Suspend Guide',
    successMessage: 'Guide has been suspended.',
  },
  'identity-activate': {
    label: 'Activate Identity',
    color: 'success',
    icon: <BadgeIcon />,
    title: 'Activate Guide Identity',
    successMessage: 'Guide identity activated.',
  },
  'identity-suspend': {
    label: 'Suspend Identity',
    color: 'warning',
    icon: <BadgeIcon />,
    title: 'Suspend Guide Identity',
    successMessage: 'Guide identity suspended.',
  },
  'identity-revoke': {
    label: 'Revoke Identity',
    color: 'error',
    icon: <BadgeIcon />,
    title: 'Revoke Guide Identity',
    successMessage: 'Guide identity permanently revoked.',
  },
};

function DocumentPreview({ doc }: { doc: GuideDocument }) {
  const isImage = /\.(jpg|jpeg|png|webp)$/i.test(doc.url) || doc.mimeType?.startsWith('image/');

  return (
    <Box sx={{ textAlign: 'center' }}>
      {isImage ? (
        <Box
          component="img"
          src={doc.url}
          alt={doc.type.replace(/_/g, ' ')}
          sx={{
            width: '100%',
            maxHeight: 220,
            objectFit: 'cover',
            borderRadius: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            cursor: 'pointer',
          }}
          onClick={() => window.open(doc.url, '_blank')}
        />
      ) : (
        <Button variant="outlined" startIcon={<InfoIcon />} href={doc.url} target="_blank" fullWidth>
          View Document
        </Button>
      )}
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, textTransform: 'capitalize' }}>
        {doc.type.replace(/_/g, ' ')}
      </Typography>
    </Box>
  );
}

function DetailField({ label, value }: { label: string; value?: string | number | null }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500}>
        {value ?? '—'}
      </Typography>
    </Box>
  );
}

export function AdminGuideReviewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // Three different lists funnel into this page, so the caller says where back
  // goes rather than this page assuming one of them.
  const backTarget =
    (location.state as { from?: string } | null)?.from ?? ADMIN_ROUTES.ONBOARDING;
  const queryClient = useQueryClient();

  const [action, setAction] = useState<ActionType | null>(null);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [verificationAction, setVerificationAction] =
    useState<VerificationAction | null>(null);
  const [verificationError, setVerificationError] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: adminKeys.review(id!),
    queryFn: () => adminApi.getGuideForReview(id!),
    enabled: !!id,
  });

  // Opening a submitted profile moves it to UNDER_REVIEW, so the queue shows
  // another admin that this one is already being looked at. Idempotent on the
  // server, and a failure here must not block the review itself.
  const startedRef = useRef(false);
  useEffect(() => {
    if (!id || startedRef.current) return;
    if (data?.profile?.profileStatus !== ProfileStatus.SUBMITTED) return;
    startedRef.current = true;
    adminApi
      .startGuideReview(id)
      .then(() => queryClient.invalidateQueries({ queryKey: adminKeys.all }))
      .catch(() => {
        /* non-fatal: the admin can still act on the profile */
      });
  }, [id, data?.profile?.profileStatus, queryClient]);

  const mutationFns: Record<ActionType, (notes: string) => Promise<unknown>> = {
    approve: (n) => adminApi.approveGuide(id!, n),
    reject: (n) => adminApi.rejectGuide(id!, n),
    'request-info': (n) => adminApi.requestMoreInfo(id!, n),
    suspend: (n) => adminApi.suspendGuide(id!, n),
    'identity-activate': (n) => adminApi.activateGuideIdentity(id!, n),
    'identity-suspend': (n) => adminApi.suspendGuideIdentity(id!, n),
    'identity-revoke': (n) => adminApi.revokeGuideIdentity(id!, n),
  };

  /**
   * Background verification lives here too, so a guide can be worked end to end
   * from one page. The pipeline rows offer the same actions as a shortcut.
   */
  const verificationMutation = useMutation({
    mutationFn: ({
      verdict,
      notes: decisionNotes,
      reference,
    }: {
      verdict: VerificationAction;
      notes: string;
      reference?: string;
    }) => {
      switch (verdict) {
        case 'pass':
          return adminApi.passVerification(id!, decisionNotes, reference);
        case 'fail':
          return adminApi.failVerification(id!, decisionNotes, reference);
        case 'retry':
          return adminApi.reopenVerification(id!, decisionNotes);
        default:
          return adminApi.startVerification(id!, decisionNotes, reference);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.all });
      setSuccessMessage('Background verification updated.');
      setVerificationAction(null);
      setVerificationError('');
    },
    onError: (err: { response?: { status?: number; data?: { message?: string } } }) => {
      setVerificationError(
        err?.response?.status === 409
          ? 'Another admin already actioned this applicant.'
          : (err?.response?.data?.message ?? 'Action failed. Please try again.')
      );
    },
  });

  const actionMutation = useMutation({
    mutationFn: async () => {
      if (!action || !notes.trim()) throw new Error('Notes are required');
      return mutationFns[action](notes);
    },
    onSuccess: () => {
      // Coarse on purpose. A decision moves the guide between pipeline tabs and
      // shifts several counts, so there is nothing worth keeping cached — and
      // the previous targeted set invalidated a key the lists did not use, so
      // acting here left them stale.
      queryClient.invalidateQueries({ queryKey: adminKeys.all });
      setSuccessMessage(ACTION_CONFIG[action!].successMessage);
      setAction(null);
      setNotes('');
      setError('');
    },
    onError: (err: { response?: { data?: { message?: string } } }) => {
      setError(err?.response?.data?.message || 'Action failed. Please try again.');
    },
  });

  if (isLoading || !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  const { profile, documents, history } = data;
  const profileUser = profile.userId as {
    phone?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    country?: string;
    phoneVerified?: boolean;
    emailVerified?: boolean;
    createdAt?: string;
  };

  const getSelfie = () => documents.find((d) => d.type === DocumentType.SELFIE);
  const getAadhaarFront = () => documents.find((d) => d.type === DocumentType.AADHAAR_FRONT);
  const getAadhaarBack = () => documents.find((d) => d.type === DocumentType.AADHAAR_BACK);
  const getCertificates = () => documents.filter((d) => d.type === DocumentType.CERTIFICATE);

  const verificationButtons: {
    verdict: VerificationAction;
    label: string;
    color: 'primary' | 'success' | 'error';
    variant: 'contained' | 'outlined';
  }[] =
    // Applicants are normally sent to the vendor as a bulk CSV from the
    // onboarding pipeline, so a PENDING applicant here may already be with
    // SpringVerify. Both verdicts are offered — and `fail` in particular has to
    // be, because the pipeline table carries no Fail action, so without it an
    // applicant who was never exported could not be failed anywhere.
    profile.verificationStatus === VerificationStatus.PENDING
      ? [
          {
            verdict: 'pass',
            label: 'Verified by SpringVerify',
            color: 'success',
            variant: 'contained',
          },
          { verdict: 'fail', label: 'Record failure', color: 'error', variant: 'outlined' },
          {
            verdict: 'send',
            label: 'Mark as sent',
            color: 'primary',
            variant: 'outlined',
          },
        ]
      : profile.verificationStatus === VerificationStatus.IN_PROGRESS
        ? [
            {
              verdict: 'pass',
              label: 'Verified by SpringVerify',
              color: 'success',
              variant: 'contained',
            },
            { verdict: 'fail', label: 'Record failure', color: 'error', variant: 'outlined' },
          ]
        : profile.verificationStatus === VerificationStatus.FAILED
          ? [
              {
                verdict: 'retry',
                label: 'Re-open verification',
                color: 'primary',
                variant: 'outlined',
              },
            ]
          : [];

  // Read the authoritative lifecycle fields, not the deprecated `status` mirror.
  const isFinalized =
    profile.profileStatus === ProfileStatus.APPROVED ||
    profile.marketplaceStatus === MarketplaceStatus.SUSPENDED;

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(backTarget)}
        sx={{ mb: 2, color: 'text.secondary' }}
      >
        Back
      </Button>

      <AdminPageHeader
        title={profile.fullName || 'Guide Review'}
        subtitle={`Application review • ${[profile.city, profile.state].filter(Boolean).join(', ') || 'Location not set'}`}
        actions={<GuideStatusChip status={profile.status} size="medium" />}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {profile.adminNotes && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Previous admin notes: {profile.adminNotes}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2.5 }}>
            <Box sx={{ display: 'flex', gap: 2.5, mb: 3 }}>
              <Avatar src={profile.profilePhoto} sx={{ width: 80, height: 80 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" fontWeight={700}>
                  {profile.fullName || '—'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {[profileUser?.firstName, profileUser?.lastName].filter(Boolean).join(' ') || 'Account name not set'}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1.5 }}>
                  {profileUser?.phone && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2">{profileUser.phone}</Typography>
                      {profileUser.phoneVerified && (
                        <Typography variant="caption" color="success.main">verified</Typography>
                      )}
                    </Box>
                  )}
                  {profileUser?.email && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2">{profileUser.email}</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>

            <Divider sx={{ mb: 2.5 }} />

            <Grid container spacing={2.5}>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <DetailField
                  label="Currently Working as a Guide"
                  value={
                    profile.currentlyWorkingAsGuide == null
                      ? undefined
                      : profile.currentlyWorkingAsGuide
                        ? 'Yes'
                        : 'No'
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <DetailField
                  label="Experience as Guide (on application)"
                  value={
                    profile.guideExperienceRange
                      ? GUIDE_EXPERIENCE_LABEL[profile.guideExperienceRange]
                      : undefined
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <DetailField label="Years of Experience" value={profile.yearsOfExperience != null ? `${profile.yearsOfExperience} years` : undefined} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <DetailField label="WhatsApp" value={profile.whatsappNumber} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <DetailField label="Country" value={profileUser?.country} />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <DetailField label="Bio" value={profile.bio} />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  Languages
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(profile.languagesSpoken ?? []).map((l) => (
                    <Typography key={l} component="span" variant="body2" sx={{ mr: 1 }}>
                      • {l}
                    </Typography>
                  ))}
                  {!profile.languagesSpoken?.length && <Typography variant="body2">—</Typography>}
                </Box>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  Specializations
                </Typography>
                <Typography variant="body2">
                  {(profile.specializations ?? []).join(', ') || '—'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  Destinations Served
                </Typography>
                <Typography variant="body2">
                  {(profile.destinationsServed ?? []).join(', ') || '—'}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <EmergencyIcon color="warning" />
              <Typography variant="subtitle1" fontWeight={700}>
                Emergency Contact
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <DetailField label="Contact Name" value={profile.emergencyContactName} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <DetailField label="Contact Number" value={profile.emergencyContactNumber} />
              </Grid>
            </Grid>
          </Paper>

          {(profile.instagramUrl || profile.facebookUrl || profile.linkedinUrl || profile.youtubeUrl) && (
            <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2.5 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
                Social Profiles
              </Typography>
              {profile.instagramUrl && <Typography variant="body2" sx={{ mb: 0.5 }}>Instagram: <a href={profile.instagramUrl} target="_blank" rel="noreferrer">{profile.instagramUrl}</a></Typography>}
              {profile.facebookUrl && <Typography variant="body2" sx={{ mb: 0.5 }}>Facebook: <a href={profile.facebookUrl} target="_blank" rel="noreferrer">{profile.facebookUrl}</a></Typography>}
              {profile.linkedinUrl && <Typography variant="body2" sx={{ mb: 0.5 }}>LinkedIn: <a href={profile.linkedinUrl} target="_blank" rel="noreferrer">{profile.linkedinUrl}</a></Typography>}
              {profile.youtubeUrl && <Typography variant="body2">YouTube: <a href={profile.youtubeUrl} target="_blank" rel="noreferrer">{profile.youtubeUrl}</a></Typography>}
            </Paper>
          )}

          <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
              Verification Documents
            </Typography>
            <Grid container spacing={2}>
              {[
                { label: 'Selfie', doc: getSelfie() },
                { label: 'Aadhaar Front', doc: getAadhaarFront() },
                { label: 'Aadhaar Back', doc: getAadhaarBack() },
              ].map(({ label, doc }) => (
                <Grid key={label} size={{ xs: 12, sm: 4 }}>
                  {doc ? (
                    <DocumentPreview doc={doc} />
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        aspectRatio: '4/3',
                        border: '2px dashed',
                        borderColor: 'error.light',
                        borderRadius: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'error.50',
                      }}
                    >
                      <Typography variant="caption" color="error.main" fontWeight={600}>
                        {label} Missing
                      </Typography>
                    </Box>
                  )}
                </Grid>
              ))}
              {getCertificates().length > 0 && (
                <Grid size={{ xs: 12 }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Certificates ({getCertificates().length})
                  </Typography>
                  <Grid container spacing={1.5}>
                    {getCertificates().map((cert) => (
                      <Grid key={cert._id} size={{ xs: 6, sm: 3 }}>
                        <DocumentPreview doc={cert} />
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Paper>

          {history.length > 0 && (
            <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2.5 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
                Verification History
              </Typography>
              {history.map((h, index) => {
                const rawAt = (h as { at?: string; createdAt?: string }).at ?? h.createdAt;
                const parsed = rawAt ? new Date(rawAt) : null;
                const dateLabel =
                  parsed && !Number.isNaN(parsed.getTime())
                    ? parsed.toLocaleString('en-IN')
                    : 'Date unavailable';
                const adminName = h.adminId?.firstName ?? 'Admin';

                return (
                <Box key={`${h.action}-${rawAt ?? index}`} sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 24 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor:
                          h.action === 'approved' ? 'success.main' :
                          h.action === 'rejected' ? 'error.main' :
                          h.action === 'suspended' ? 'grey.500' : 'warning.main',
                        mt: 0.5,
                      }}
                    />
                    {index < history.length - 1 && (
                      <Box sx={{ flex: 1, width: 2, bgcolor: 'divider', my: 0.5 }} />
                    )}
                  </Box>
                  <Box sx={{ pb: 2, flex: 1 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                      {h.action.replace(/_/g, ' ')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {dateLabel} • By {adminName}
                    </Typography>
                    {h.notes && <Typography variant="body2" sx={{ mt: 0.5 }}>{h.notes}</Typography>}
                  </Box>
                </Box>
                );
              })}
            </Paper>
          )}
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2.5,
              position: { lg: 'sticky' },
              top: { lg: 88 },
            }}
          >
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              Background verification
            </Typography>
            <Box sx={{ mb: 1.5 }}>
              <StatusBadge
                status={verificationStatusToBadge(profile.verificationStatus)}
                label={formatStatusLabel(profile.verificationStatus ?? 'not started')}
              />
            </Box>
            <Box sx={{ mb: 2 }}>
              {profile.verificationReference && (
                <Typography variant="caption" color="text.secondary" display="block">
                  SpringVerify ref: {profile.verificationReference}
                </Typography>
              )}
              {profile.verificationSentAt && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Sent {new Date(profile.verificationSentAt).toLocaleDateString()}
                </Typography>
              )}
              {profile.verificationDecidedAt && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Decided {new Date(profile.verificationDecidedAt).toLocaleDateString()}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
              {verificationButtons.map((button) => (
                <Button
                  key={button.verdict}
                  variant={button.variant}
                  color={button.color}
                  onClick={() => {
                    setVerificationError('');
                    setVerificationAction(button.verdict);
                  }}
                  fullWidth
                  sx={{ justifyContent: 'flex-start', py: 1.25 }}
                >
                  {button.label}
                </Button>
              ))}
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
              Review Actions
            </Typography>

            {isFinalized ? (
              <Alert severity={profile.status === GuideStatus.APPROVED ? 'success' : 'warning'} sx={{ mb: 2 }}>
                This application has been {profile.status?.replace(/_/g, ' ')}.
              </Alert>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {(['approve', 'reject', 'request-info', 'suspend'] as ActionType[]).map((act) => {
                  const cfg = ACTION_CONFIG[act];
                  return (
                    <Button
                      key={act}
                      variant={act === 'approve' ? 'contained' : 'outlined'}
                      color={cfg.color}
                      startIcon={cfg.icon}
                      onClick={() => {
                        setAction(act);
                        setError('');
                      }}
                      fullWidth
                      sx={{ justifyContent: 'flex-start', py: 1.25 }}
                    >
                      {cfg.label}
                    </Button>
                  );
                })}
              </Box>
            )}

            <Divider sx={{ my: 2.5 }} />

            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
              Trplin Guide Identity
            </Typography>
            {profile.identity?.guideId ? (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="body2"
                  fontWeight={800}
                  sx={{ fontFamily: 'ui-monospace, Menlo, Monaco, Consolas, monospace', mb: 0.5 }}
                >
                  {profile.identity.guideId}
                </Typography>
                <Chip
                  size="small"
                  label={profile.identity.status}
                  color={
                    profile.identity.status === GuideIdentityStatus.ACTIVE
                      ? 'success'
                      : profile.identity.status === GuideIdentityStatus.REVOKED
                        ? 'error'
                        : 'warning'
                  }
                  sx={{ fontWeight: 700, mb: 1.5, mr: 1 }}
                />
                <IdentityCardStateChip state={profile.identity.cardState} sx={{ mb: 1.5 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<EditNoteIcon />}
                    onClick={() => navigate(ADMIN_ROUTES.identityCard(id!))}
                    fullWidth
                  >
                    {profile.identity.cardState === 'unpublished' ? 'Prepare & publish card' : 'Edit identity card'}
                  </Button>
                  {profile.identity.cardState !== 'unpublished' && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<OpenInNewIcon />}
                      href={`/g/${profile.identity.code}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      fullWidth
                    >
                      View Identity
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ContentCopyIcon />}
                    onClick={async () => {
                      const url =
                        profile.identity?.identityUrl ||
                        `${window.location.origin}/g/${profile.identity?.code}`;
                      const ok = await copyTextToClipboard(url);
                      setSuccessMessage(ok ? 'Identity URL copied' : 'Unable to copy URL');
                    }}
                    fullWidth
                  >
                    Copy URL
                  </Button>
                  {profile.identity.status !== GuideIdentityStatus.ACTIVE &&
                    profile.identity.status !== GuideIdentityStatus.REVOKED && (
                      <Button
                        size="small"
                        color="success"
                        variant="outlined"
                        onClick={() => {
                          setAction('identity-activate');
                          setError('');
                        }}
                        fullWidth
                      >
                        Activate Identity
                      </Button>
                    )}
                  {profile.identity.status === GuideIdentityStatus.ACTIVE && (
                    <Button
                      size="small"
                      color="warning"
                      variant="outlined"
                      onClick={() => {
                        setAction('identity-suspend');
                        setError('');
                      }}
                      fullWidth
                    >
                      Suspend Identity
                    </Button>
                  )}
                  {profile.identity.status !== GuideIdentityStatus.REVOKED && (
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      onClick={() => {
                        setAction('identity-revoke');
                        setError('');
                      }}
                      fullWidth
                    >
                      Revoke Identity
                    </Button>
                  )}
                </Box>
              </Box>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                An identity is issued when the guide is approved. Its card goes public only after you
                prepare and publish it here.
              </Alert>
            )}

            <Divider sx={{ my: 2.5 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <DetailField
                label="Submitted"
                value={profile.submittedAt ? new Date(profile.submittedAt).toLocaleString('en-IN') : undefined}
              />
              <DetailField
                label="Last Reviewed"
                value={profile.reviewedAt ? new Date(profile.reviewedAt).toLocaleString('en-IN') : undefined}
              />
              <DetailField label="Onboarding Step" value={profile.onboardingStep} />
              <DetailField label="Account Created" value={profileUser?.createdAt ? new Date(profileUser.createdAt).toLocaleString('en-IN') : undefined} />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={!!action} onClose={() => setAction(null)} maxWidth="sm" fullWidth>
        {action && (
          <>
            <DialogTitle fontWeight={700}>{ACTION_CONFIG[action].title}</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Add notes for <strong>{profile.fullName}</strong>. These will be saved and shared with the guide where applicable.
              </Typography>
              <TextField
                label="Admin Notes"
                multiline
                minRows={4}
                fullWidth
                autoFocus
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Explain your decision clearly..."
                helperText="Required"
              />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => { setAction(null); setNotes(''); }}>Cancel</Button>
              <Button
                variant="contained"
                color={ACTION_CONFIG[action].color}
                onClick={() => actionMutation.mutate()}
                disabled={!notes.trim() || actionMutation.isPending}
                startIcon={actionMutation.isPending ? <CircularProgress size={16} color="inherit" /> : ACTION_CONFIG[action].icon}
              >
                Confirm {ACTION_CONFIG[action].label}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <VerificationActionDialog
        action={verificationAction}
        guideName={profile.fullName}
        existingReference={profile.verificationReference}
        isPending={verificationMutation.isPending}
        error={verificationError}
        onClose={() => {
          setVerificationAction(null);
          setVerificationError('');
        }}
        onConfirm={({ notes: decisionNotes, reference }) => {
          if (!verificationAction) return;
          verificationMutation.mutate({
            verdict: verificationAction,
            notes: decisionNotes,
            reference,
          });
        }}
      />

      <Snackbar
        open={!!successMessage}
        autoHideDuration={5000}
        onClose={() => setSuccessMessage('')}
        message={successMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
